import { useEffect, useState } from 'react'
import ReactMarkdown from 'react-markdown'

interface MarkdownRendererProps {
  content: string
  onTocChange: (toc: Array<{ id: string; level: number; text: string }>) => void
}

function CodeBlock({ children }: { children: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(children)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  return (
    <div className="relative group mb-4">
      <button
        onClick={handleCopy}
        className="absolute top-2 right-2 p-2 bg-gray-700 hover:bg-gray-600 rounded text-gray-300 hover:text-white transition-colors opacity-0 group-hover:opacity-100"
        aria-label="Copy code"
      >
        {copied ? (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
          </svg>
        )}
      </button>
      <code className="block bg-gray-800 text-gray-300 p-4 rounded-lg overflow-x-auto text-sm font-mono">
        {children}
      </code>
    </div>
  )
}

export default function MarkdownRenderer({ content, onTocChange }: MarkdownRendererProps) {
  const [processedContent, setProcessedContent] = useState('')

  useEffect(() => {
    // Extract headings and generate IDs
    const toc: Array<{ id: string; level: number; text: string }> = []
    const lines = content.split('\n')
    const processedLines: string[] = []

    for (const line of lines) {
      const match = line.match(/^(#{1,6})\s+(.+)$/)
      if (match) {
        const level = match[1].length
        const text = match[2].trim()
        const id = text
          .toLowerCase()
          .replace(/[^\w\s-]/g, '')
          .replace(/\s+/g, '-')

        toc.push({ id, level, text })
        processedLines.push(`${match[1]} ${text} {#${id}}`)
      } else {
        processedLines.push(line)
      }
    }

    setProcessedContent(processedLines.join('\n'))
    onTocChange(toc)
  }, [content, onTocChange])

  return (
    <div className="prose prose-invert prose-slate max-w-none">
      <ReactMarkdown
        components={{
          h1: ({ children }) => {
            const text = String(children)
            const match = text.match(/^(.+?)\s+\{#(.+?)\}$/)
            if (match) {
              return <h1 id={match[2]} className="text-3xl font-bold mt-8 mb-4 text-white scroll-mt-4">{match[1]}</h1>
            }
            return <h1 className="text-3xl font-bold mt-8 mb-4 text-white">{children}</h1>
          },
          h2: ({ children }) => {
            const text = String(children)
            const match = text.match(/^(.+?)\s+\{#(.+?)\}$/)
            if (match) {
              return <h2 id={match[2]} className="text-2xl font-bold mt-6 mb-3 text-white scroll-mt-4">{match[1]}</h2>
            }
            return <h2 className="text-2xl font-bold mt-6 mb-3 text-white">{children}</h2>
          },
          h3: ({ children }) => {
            const text = String(children)
            const match = text.match(/^(.+?)\s+\{#(.+?)\}$/)
            if (match) {
              return <h3 id={match[2]} className="text-xl font-bold mt-5 mb-2 text-white scroll-mt-4">{match[1]}</h3>
            }
            return <h3 className="text-xl font-bold mt-5 mb-2 text-white">{children}</h3>
          },
          h4: ({ children }) => {
            const text = String(children)
            const match = text.match(/^(.+?)\s+\{#(.+?)\}$/)
            if (match) {
              return <h4 id={match[2]} className="text-lg font-semibold mt-4 mb-2 text-white scroll-mt-4">{match[1]}</h4>
            }
            return <h4 className="text-lg font-semibold mt-4 mb-2 text-white">{children}</h4>
          },
          h5: ({ children }) => {
            const text = String(children)
            const match = text.match(/^(.+?)\s+\{#(.+?)\}$/)
            if (match) {
              return <h5 id={match[2]} className="text-base font-semibold mt-3 mb-2 text-gray-200 scroll-mt-4">{match[1]}</h5>
            }
            return <h5 className="text-base font-semibold mt-3 mb-2 text-gray-200">{children}</h5>
          },
          h6: ({ children }) => {
            const text = String(children)
            const match = text.match(/^(.+?)\s+\{#(.+?)\}$/)
            if (match) {
              return <h6 id={match[2]} className="text-sm font-semibold mt-3 mb-2 text-gray-300 scroll-mt-4">{match[1]}</h6>
            }
            return <h6 className="text-sm font-semibold mt-3 mb-2 text-gray-300">{children}</h6>
          },
          p: ({ children }) => <p className="mb-4 text-gray-300 leading-relaxed">{children}</p>,
          ul: ({ children }) => <ul className="list-disc list-inside mb-4 text-gray-300 space-y-1">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal list-inside mb-4 text-gray-300 space-y-1">{children}</ol>,
          li: ({ children }) => <li className="ml-4">{children}</li>,
          code: ({ inline, children }: any) => {
            if (inline) {
              return <code className="bg-gray-800 text-blue-400 px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>
            }
            return <CodeBlock>{String(children).replace(/\n$/, '')}</CodeBlock>
          },
          pre: ({ children }) => <pre className="mb-4">{children}</pre>,
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-blue-600 pl-4 mb-4 text-gray-400 italic">
              {children}
            </blockquote>
          ),
          a: ({ href, children }) => (
            <a href={href} className="text-blue-400 hover:text-blue-300 underline" target="_blank" rel="noopener noreferrer">
              {children}
            </a>
          ),
          table: ({ children }) => (
            <div className="overflow-x-auto mb-4">
              <table className="min-w-full border border-gray-700">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className="bg-gray-800">{children}</thead>,
          tbody: ({ children }) => <tbody className="bg-gray-900">{children}</tbody>,
          tr: ({ children }) => <tr className="border-b border-gray-700">{children}</tr>,
          th: ({ children }) => <th className="px-4 py-2 text-left text-gray-300 font-semibold">{children}</th>,
          td: ({ children }) => <td className="px-4 py-2 text-gray-400">{children}</td>,
          hr: () => <hr className="my-6 border-gray-700" />,
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  )
}
